import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpayCheckout";

const errorText = (err, fallback) => typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback;
export const usePaymentOptions = (order, onUpdated) => {
  const [methods, setMethods] = useState(null), [choice, setChoice] = useState(null), [upi, setUpi] = useState(null);
  const [reference, setReference] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/payments/methods`, { signal: controller.signal }).then(({ data }) => {
      setMethods(data); setChoice(data.upi.enabled ? "upi" : data.razorpay.enabled ? "razorpay" : "whatsapp");
    }).catch((err) => { if (!axios.isCancel(err)) { setError("Online payment options could not be loaded. WhatsApp confirmation is still available."); setMethods({ upi: { enabled: false }, razorpay: { enabled: false }, whatsapp: true }); setChoice("whatsapp"); } });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    setUpi(null); setReference("");
    if (choice !== "upi" || ["paid", "verification_pending"].includes(order.payment_status) || order.status === "cancelled") return;
    const controller = new AbortController();
    axios.get(`${API}/payments/upi/${order.order_id}`, { signal: controller.signal }).then(({ data }) => { setUpi(data); setError(""); })
      .catch((err) => { if (!axios.isCancel(err)) setError("UPI details could not be loaded. Please choose another payment option."); });
    return () => controller.abort();
  }, [choice, order.order_id, order.payment_status, order.status]);
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy unavailable. Please copy the payment address manually."); }
  };
  const claimUpi = async () => {
    const value = reference.trim();
    if (!/^[A-Za-z0-9-]{6,40}$/.test(value)) { toast.error("Enter a valid UPI transaction / UTR reference"); return; }
    setBusy(true);
    try { await axios.post(`${API}/payments/upi/${order.order_id}/claim`, { reference: value }); toast.success("Payment reference received. We'll verify and confirm shortly!"); onUpdated(); }
    catch (err) { toast.error(errorText(err, "Could not submit the reference")); }
    finally { setBusy(false); }
  };
  const payRazorpay = async () => {
    setBusy(true);
    try { await openRazorpay(order, () => { toast.success("Payment successful!"); onUpdated(); }, toast.error); }
    catch (err) { toast.error(errorText(err, "Razorpay is unavailable right now")); }
    finally { setBusy(false); }
  };
  return { methods, choice, setChoice, upi, reference, setReference, busy, error, copy, claimUpi, payRazorpay };
};